import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Card, Typography, message, Row, Col,
  Tag, Tooltip, Modal, Form, Input, Select, Switch, Grid,
} from 'antd';
import { PlusOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import api from '../../api/axios';

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const KullanicilarPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.md;

  // ─── Veri ────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/kullanicilar');
      const arr = Array.isArray(res.data) ? res.data : res.data.data || [];
      setData(arr);
    } catch {
      message.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Modal ────────────────────────────────────────────────────────────────

  const openModal = (item = null) => {
    setEditItem(item);
    if (item) {
      form.setFieldsValue({ ...item, sifre: '' });
    } else {
      form.resetFields();
      form.setFieldsValue({ rol: 'Danışman', aktif: true });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = { ...values };
      if (!payload.sifre) delete payload.sifre;

      if (editItem) {
        await api.put(`/kullanicilar/${editItem.id}`, payload);
        message.success('Kullanıcı güncellendi');
      } else {
        await api.post('/kullanicilar', payload);
        message.success('Kullanıcı eklendi');
      }
      closeModal();
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAktif = async (record) => {
    try {
      await api.put(`/kullanicilar/${record.id}`, { ...record, aktif: !record.aktif });
      message.success(record.aktif ? 'Kullanıcı pasife alındı' : 'Kullanıcı aktive edildi');
      fetchData();
    } catch {
      message.error('İşlem başarısız');
    }
  };

  // ─── Sütunlar ─────────────────────────────────────────────────────────────

  const columns = [
    {
      title: 'Ad Soyad',
      dataIndex: 'adSoyad',
      key: 'adSoyad',
      render: (t) => <strong>{t || '-'}</strong>,
      sorter: (a, b) => (a.adSoyad || '').localeCompare(b.adSoyad || ''),
    },
    {
      title: 'Kullanıcı Kodu',
      dataIndex: 'userKodu',
      key: 'userKodu',
      responsive: ['sm'], // < 576px'de gizle
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      render: (v) => (
        <Tag color={v === 'Yönetici' ? 'gold' : 'blue'}>{v || '-'}</Tag>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'telefon',
      key: 'telefon',
      responsive: ['md'], // < 768px'de gizle
    },
    {
      title: 'Aktif',
      dataIndex: 'aktif',
      key: 'aktif',
      align: 'center',
      width: 70,
      render: (v, record) => (
        <Switch
          checked={v}
          size="small"
          onChange={() => handleToggleAktif(record)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 60,
      render: (_, record) => (
        <Tooltip title="Düzenle">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
        </Tooltip>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <Row justify="space-between" align="middle" gutter={[0, 8]} style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <TeamOutlined style={{ fontSize: 20, color: '#1677ff' }} />
            <Title level={4} style={{ margin: 0 }}>Kullanıcılar</Title>
          </Space>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            {isMobile ? 'Yeni' : 'Yeni Kullanıcı'}
          </Button>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          bordered
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 400 }}
          pagination={{
            pageSize: 15,
            showTotal: (t) => `Toplam ${t} kullanıcı`,
            simple: isMobile,
          }}
          locale={{ emptyText: 'Kullanıcı bulunamadı' }}
        />
      </Card>

      <Modal
        title={editItem ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        width={isMobile ? '100vw' : isTablet ? '90vw' : 500}
        style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
        styles={{ body: { padding: isMobile ? '16px 12px' : undefined } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="adSoyad"
                label="Ad Soyad"
                rules={[{ required: true, message: 'Ad soyad zorunludur' }]}
              >
                <Input placeholder="Ad soyad" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="userKodu"
                label="Kullanıcı Kodu"
                rules={[{ required: true, message: 'Kullanıcı kodu zorunludur' }]}
              >
                <Input placeholder="Kullanıcı kodu" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="sifre"
                label={editItem ? 'Şifre (değiştirmek için girin)' : 'Şifre'}
                rules={
                  editItem
                    ? []
                    : [
                        { required: true, message: 'Şifre zorunludur' },
                        { min: 6, message: 'En az 6 karakter' },
                      ]
                }
              >
                <Input.Password placeholder="Şifre" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="rol"
                label="Rol"
                rules={[{ required: true, message: 'Rol seçin' }]}
              >
                <Select>
                  <Option value="Yönetici">Yönetici</Option>
                  <Option value="Danışman">Danışman</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="telefon" label="Telefon">
                <Input placeholder="05xx xxx xxxx" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="aktif" label="Aktif" valuePropName="checked">
                <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end">
            <Space>
              <Button onClick={closeModal}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editItem ? 'Güncelle' : 'Kaydet'}
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default KullanicilarPage;